import { useState, useEffect, useCallback } from 'react'
import { benefitRulesService } from '../services/benefitRulesService'
import type { BenefitRule, BenefitRuleFilters, TriggerType, ActionType } from '../types'

export function useBenefitRules(initialFilters?: Partial<BenefitRuleFilters>) {
  const [data, setData] = useState<BenefitRule[]>([])
  const [total, setTotal] = useState(0)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [filters, setFilters] = useState<BenefitRuleFilters>({
    page: 1,
    limit: 20,
    ...initialFilters,
  })

  const fetchData = useCallback(async () => {
    setIsLoading(true)
    setError(null)
    try {
      const response = await benefitRulesService.getAll(filters)
      setData(response.data)
      setTotal(response.total_items)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al cargar reglas de beneficio')
    } finally {
      setIsLoading(false)
    }
  }, [filters])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  const setPage = (page: number) => setFilters((prev) => ({ ...prev, page }))
  const setLimit = (limit: number) => setFilters((prev) => ({ ...prev, limit, page: 1 }))

  const setActiveFilter = (isActive: boolean | undefined) =>
    setFilters((prev) => ({ ...prev, is_active: isActive, page: 1 }))

  const setTriggerFilter = (triggerType: TriggerType | undefined) =>
    setFilters((prev) => ({ ...prev, trigger_type: triggerType, page: 1 }))

  const setActionFilter = (actionType: ActionType | undefined) =>
    setFilters((prev) => ({ ...prev, action_type: actionType, page: 1 }))

  const applyFilters = (partial: Partial<BenefitRuleFilters>) =>
    setFilters((prev) => ({ ...prev, ...partial, page: 1 }))

  const clearFilters = () =>
    setFilters({ page: 1, limit: filters.limit })

  return {
    data,
    total,
    isLoading,
    error,
    filters,
    setPage,
    setLimit,
    setActiveFilter,
    setTriggerFilter,
    setActionFilter,
    applyFilters,
    clearFilters,
    refetch: fetchData,
  }
}
